import { Above } from '@jetshop/ui/Breakpoints';
import loadable from '@loadable/component';
import { styled } from 'linaria/react';
import React from 'react';
import { StyledPrice } from '../CategoryPage/ProductCard';
import { productPageUspComponents } from '../ContentEditor/ContentEditorComponents';
import { DynamicContentRenderer } from '../ContentEditor/DynamicContentRenderer';
import { GoldBar } from '../Layout/GoldBar';
import { Favourite } from '../ProductList/Favourite';
import { theme } from '../Theme';
import Spinner from '../ui/Spinner';
import AddToCartForm from './AddToCart/AddToCartForm';
import WarehouseStock from './StockStatus/WarehouseStock';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import BrandsQuery from './BrandsQuery.gql';
// import { LipscoreProductRating } from '../Lipscore/LipscoreProductRating';
import ProductInfoAccordion from './ProductInfoAccordion';
import GiftCardAddToCartForm from './AddToCart/GiftCardAddToCartForm';

const LoadablePackageProduct = loadable(
  () => import('./PackageProduct/PackageProduct'),
  {
    fallback: <Spinner />
  }
);

const ProductPageInfoCardContainer = styled('div')`
  padding: 0;
`;

const ProductPageInfoCardWrapper = styled('div')`
  height: 100%;
  background: white;
  padding: 50px 40px;
  position: relative;

  ${theme.below.lg} {
    padding: 40px 1rem 20px 1rem;
  }

  .brand-name {
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.03em;
    color: ${theme.colors.darkgrey};
    text-decoration: none;
  }

  h1 {
    font-family: ${theme.fonts.secondary};
    font-style: normal;
    font-weight: 500;
    font-size: 24px;
    line-height: 29px;
    letter-spacing: 0.03em;
    text-transform: capitalize;
    color: ${theme.colors.darkgrey};
  }

  .gold-bar:after {
    left: 0;
    transform: translateX(0%);
  }
`;

const MainRowWrapper = styled('div')`
  display: flex;
  width: 100%;
  justify-content: space-between;
  margin-bottom: 20px;

  h1 {
    text-transform: uppercase;
    ${theme.below.lg} {
      font-size: 20px;
      line-height: 24px;
    }
  }
  .pricewrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    ${theme.above.lg} {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      justify-content: center;
      margin-bottom: 10px;
    }
  }

  ${theme.below.lg} {
    align-items: flex-start;
  }

  .gold-bar {
    flex: 3;
  }
`;

const ProductPageInfoCardPrice = styled(StyledPrice)`
  font-size: 20px;
  line-height: 20px;
  justify-content: center;
  flex-direction: column;
  font-weight: 500;

  &.old-price {
    text-decoration: line-through;
    text-decoration-color: #848484;
  }

  &.new-price {
    div {
      font-weight: 700;
      color: ${theme.colors.gold} !important;
    }
  }

  ${theme.below.lg} {
    justify-content: flex-start;
  }
`;
const UspRow = styled('div')`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  flex-wrap: wrap;
  /* margin-top: 10px; */
  margin-bottom: 0px;

  ${theme.below.lg} {
    justify-content: flex-start;
  }
  > div {
    margin-top: 0;
    justify-content: flex-start;

    ${theme.below.lg} {
      flex-direction: column;
      > div {
        margin-bottom: 10px;
      }
    }
  }

  .html-content {
    font-weight: 400;
    font-size: 14px;
    line-height: 24px;
    letter-spacing: 0.03em;
    margin-right: 5px;

    a {
      text-decoration: none;
    }
  }
`;

const BadgeWrapper = styled('div')`
  max-width: fit-content;
  margin-left: auto;
  position: absolute;
  display: flex;
  width: 100%;
  top: 14px;
  left: 40px;

  ${theme.below.lg} {
    left: 14px;
  }
`;

const Badge = styled('div')`
  background: ${theme.colors.gold};
  min-width: 50px;
  /* max-width: 140px; */
  text-align: center;
  font-weight: bold;
  font-size: 12px;
  line-height: 22px;
  color: #fff;
  padding: 0 10px;
  margin-right: 10px;
`;

const AdditionalInformationWrapper = styled('div')`
  margin-top: 0;
  background-color: #f1eae0;
  padding: 20px 20px 10px 20px;
`;

export const ProductPageInfoCard = ({
  product,
  variant,
  variantHandler,
  getMissingOptions,
  selectedVariation,
  parents
}) => {
  // const translate = useIntl();

  const brandName = product?.customFields?.find(({ key }) => key === 'merke')
    ?.stringValue;

  const brandsCategoryResult = useQuery(BrandsQuery, {
    variables: {
      id: 329
    }
  });

  //Brand information
  const brandInformationData = brandsCategoryResult?.data?.category?.subcategories?.find(
    subcat => subcat?.name === brandName
  );

  const brandContent = brandsCategoryResult?.data?.category?.subcategories?.find(
    subcat => subcat?.id === brandInformationData?.id
  );

  //Latest product badge
  const lastProductBadge =
    parseInt(selectedVariation?.stockStatus?.text?.replace(/\D/g, '')) === 1;

  //Calculations for Newsbagdge
  const today = Date.now();
  const productDate = Date.parse(product?.stockStatus?.stockDate.split('T')[0]);
  const daysTotal = today - productDate;
  const newsBadge = Math.ceil(daysTotal / (1000 * 3600 * 24)) < 15;

  const discountPrice =
    product?.previousPrice?.incVat !== product?.price?.incVat;

  const bonusPrice = Math.ceil(product.price?.incVat * 0.15);

  const priceBadge =
    Math.round(
      100 -
        (Math.round(product.price?.incVat) / product?.previousPrice?.incVat) *
          100
    ) || 0;

  const linkedBrandName = () => {
    // Finds Brand subcategory with correct name
    const brandCategory = brandsCategoryResult?.data?.category?.subcategories?.find(
      brand => brand?.name?.toLowerCase() === brandName?.toLowerCase()
    );
    const brandLink = brandCategory?.primaryRoute?.path;

    //Checking what kind of element to return
    if (brandName) {
      if (brandLink) {
        return (
          <Link className={'brand-name'} to={brandLink}>
            {brandName}
          </Link>
        );
      }
      return <h2 className={'brand-name'}>{brandName}</h2>;
    }

    if (product?.subName) {
      return <h2 className={'brand-name'}>{product?.subName}</h2>;
    }
  };

  const giftCardProducts = ['9633710', '9633713', '9633717'];
  const isGiftCard = giftCardProducts.includes(product?.articleNumber);

  return (
    <ProductPageInfoCardContainer>
      <ProductPageInfoCardWrapper className="product-details">
        <Above breakpoint="lg">
          {matches =>
            matches ? (
              <>
                <header>
                  <div>
                    <BadgeWrapper>
                      {lastProductBadge && (
                        <Badge>
                          <p>Kun 1 igjen</p>
                        </Badge>
                      )}
                      {newsBadge && (
                        <Badge>
                          <p>Nyheter</p>
                        </Badge>
                      )}
                      {discountPrice ? (
                        <Badge>
                          <p>{priceBadge + '%'}</p>
                        </Badge>
                      ) : (
                        <Badge>
                          <p>Opptjen {bonusPrice} kr i bonus</p>
                        </Badge>
                      )}
                    </BadgeWrapper>
                    <Favourite
                      product={product}
                      variant={product?.variant}
                      style={{
                        float: 'right',
                        marginLeft: 'auto',
                        fontSize: '1em',
                        position: 'absolute',
                        top: '7px',
                        right: '14px'
                      }}
                    />
                  </div>
                  <MainRowWrapper>
                    <div className="gold-bar">
                      <GoldBar>
                        {linkedBrandName()}
                        <h1 data-testid="product-title">
                          {product?.name || 'Loading...'}
                        </h1>
                      </GoldBar>
                      {/* {product && <LipscoreProductRating product={product} />} */}
                    </div>
                    {!isGiftCard && (
                      <div className="pricewrapper">
                        {discountPrice ? (
                          <>
                            <ProductPageInfoCardPrice
                              price={product.previousPrice}
                              className="old-price"
                            />
                            <ProductPageInfoCardPrice
                              price={product.price}
                              className="new-price"
                            />
                          </>
                        ) : (
                          <ProductPageInfoCardPrice
                            price={product.price}
                            className="normal-price"
                          />
                        )}
                      </div>
                    )}
                  </MainRowWrapper>
                </header>
              </>
            ) : (
              <>
                <BadgeWrapper>
                  {newsBadge && (
                    <Badge>
                      <p>Nyheter</p>
                    </Badge>
                  )}
                  {discountPrice ? (
                    <Badge>
                      <p>{priceBadge + '%'}</p>
                    </Badge>
                  ) : (
                    <Badge>
                      <p>Opptjen {bonusPrice} kr. i bonus</p>
                    </Badge>
                  )}
                </BadgeWrapper>
                <Favourite
                  product={product}
                  variant={product?.variant}
                  style={{
                    float: 'right',
                    marginLeft: 'auto',
                    fontSize: '1em',
                    position: 'absolute',
                    top: '7px',
                    right: '14px'
                  }}
                />
                <header>
                  <MainRowWrapper>
                    <div className="gold-bar">
                      <GoldBar>
                        {linkedBrandName()}
                        <h1 data-testid="product-title">
                          {product?.name || 'Loading...'}
                        </h1>
                      </GoldBar>
                      {/* {product && <LipscoreProductRating product={product} />} */}
                    </div>
                    {!isGiftCard && (
                      <div className="pricewrapper">
                        {product?.previousPrice?.incVat !==
                        product?.price?.incVat ? (
                          <>
                            <ProductPageInfoCardPrice
                              price={product.previousPrice}
                              className="old-price"
                            />
                            <ProductPageInfoCardPrice
                              price={product.price}
                              className="new-price"
                            />
                          </>
                        ) : (
                          <ProductPageInfoCardPrice
                            price={product.price}
                            className="normal-price"
                          />
                        )}
                      </div>
                    )}
                  </MainRowWrapper>
                </header>
              </>
            )
          }
        </Above>
        {product?.isPackage ? (
          <LoadablePackageProduct product={product} />
        ) : (
          <>
            {isGiftCard ? (
              <GiftCardAddToCartForm
                product={product}
                variant={variant}
                getMissingOptions={getMissingOptions}
                variantHandler={variantHandler}
              />
            ) : (
              <AddToCartForm
                product={product}
                variant={variant}
                getMissingOptions={getMissingOptions}
                variantHandler={variantHandler}
              />
            )}
          </>
        )}
        <AdditionalInformationWrapper>
          {variantHandler?.validation === 'valid' && (
            <WarehouseStock
              product={product}
              variantHandler={variantHandler}
            ></WarehouseStock>
          )}
          <UspRow>
            <DynamicContentRenderer
              categoryId={283}
              rendererComponents={productPageUspComponents}
            />
          </UspRow>
        </AdditionalInformationWrapper>
        <ProductInfoAccordion
          product={product}
          selectedVariation={selectedVariation}
          brand={brandInformationData}
          brandContent={brandContent?.content}
        />
      </ProductPageInfoCardWrapper>
    </ProductPageInfoCardContainer>
  );
};
